import {
  ReportLayerAnalysisResponse,
  ReportSectionData,
  ReportSubSectionData,
  ReportSectionResponse,
} from "./reportTypes";

export type ReportDataAPIResponseTotals = {
  count?: number;
  geocoded_count?: number;
  tiv?: number;
  tiv_building?: number;
  tiv_contents?: number;
  tiv_other?: number;
  tiv_bi?: number;
  amount_subject?: number;
  avg_dollar_per_sqft?: number;
  buildings_missing_area?: number;
  buildings_missing_tiv?: number;
  buildings_with_area_and_tiv?: number;
  total_building_area?: number;
};

export type ReportDataAPIResponseOverview = {
  total_locations?: number;
};

export type ReportDataAPIResponse = {
  totals: ReportDataAPIResponseTotals;
  layer_analysis: ReportLayerAnalysisResponse;
  breakdown: ReportSectionData[];
  country_breakdown: ReportSubSectionData[];
  state_breakdown: ReportSubSectionData[];
  sections: ReportSectionResponse[];
  overview: ReportDataAPIResponseOverview;

  status: string;
  status_reason: string;
  pct_complete: number;
  filename: string;
  insured_name: string | null;
};

export type PingMapsLayerIntegration =
  | "DEFAULT"
  | "NEARMAP"
  | "WIND"
  | "EAGLEVIEW"
  | "EQ"
  | "FLOOD"
  | "SLOSH"
  | "USDA_WILDFIRE"
  | "USDA_ENHANCED_RPS"
  | "NRI_WILDFIRE"
  | "CORELOGIC"
  | "INTERMAP"
  | "PRECISELY"
  | "KATRISK"
  | "MUNICIPAL"
  | "US_LEVEES"
  | "VEXCEL"
  | "EASI_CRIME"
  | "SWISSRE_CATNET"
  | "NOAA_SPC"
  | "USGS_PROB_MMI"
  | "TERRAFUSE_WILDFIRE"
  | "NHC_TROPICAL"
  | "USGS_WBD"
  | "NIFC_WFIGS_CURRENT"
  | "FIRE_PERIMETERS"
  | "FL_SINKHOLES"
  | "HISTORICAL_HURRICANE_TRACKS";

export interface LayerInfo {
  name: string;
  url: string;
}

export type LayersConfig = Partial<Record<PingMapsLayerIntegration, LayerInfo>>;
export type IntegrationsConfig = Partial<Record<string, string>>;

export interface LayerSettingsAPIResponse {
  pingmaps: {
    layers: LayersConfig;
    integrations: IntegrationsConfig;
    required_fields?: Record<string, Array<string | string>>;
    aoa_input_options?: Record<string, Record<string, Array<{ label: string; value: string | number }>>>;
  };
}

export const PERMISSIONS = {
  CanAccessPingLocation: "can_access_ping_location",
  CanAccessPingLocationExtension: "can_access_ping_location_extension",
  CanAccessPingMaps: "can_access_ping_maps",
  CanEditBuildingFields: "can_edit_building_fields",
  CanAccessPingActivityLog: "can_access_ping_activity_log",
  CanAccessPingVision: "can_access_ping_vision",
  CanAccessPingSubmissionPortal: "can_access_ping_submission_portal",
  CanAccessDeveloperTools: "can_access_developer_tools",
  CanAccessPolicyTerms: "can_access_policy_terms",
  CanAccessAllOverlays: "can_access_all_overlays",
  CanAccessAllIntegrations: "can_access_all_integrations",
  CanAccessQueueMonitor: "can_access_queue_monitor",
  CanCreateApiKeys: "can_create_api_keys",
  CanAccessPingLocationPdfDownload: "can_access_ping_location_pdf_download",
  CanAccessPingLocationHtmlPdfDownload: "can_access_ping_location_html_pdf_download",
  CanAccessMapsDownloadOutputters: "can_access_maps_download_outputters",
  CanAccessMapsPptxDownload: "can_access_maps_pptx_download",
  CanAccessMudmaps: "can_access_mudmaps",
  CanAccessAllHitlWorkQueue: "can_access_all_hitl_work_queue",
} as const;

export type PermissionKey = (typeof PERMISSIONS)[keyof typeof PERMISSIONS];

export type MeApiResponse = {
  username: string;
  email: string;
  company_name: string;
  company_short_name?: string | null;
  company_id?: string | null;
  team_id?: string | null;
  team_name: string;
  division_id?: string | null;
  division_name: string;
  first_name: string;
  last_name: string;
  permissions: string[];
  is_ping_employee: boolean;
  is_staff: boolean;
  memberships?: unknown[];
  temporary_memberships?: unknown[];
  primary_team_id?: string | null;
  can_view_all_teams?: boolean;
  impersonator_user_id?: string | null;
  impersonator_email?: string | null;
  has_accepted_terms_of_service_version: string | null;
  has_accepted_terms_of_service_date?: string | null;
  posthog_guid?: string;
  analytics_consent?: boolean | null;
  analytics_consent_version?: string | null;
  analytics_consent_updated_at?: string | null;
  ping_data_plan?: string | null;
  has_scrubbing_features?: boolean;
  can_access_loss_runs?: boolean;
};

export type DropDownOptionType = {
  value: string | number;
  count: number;
  count_pct: number;
  tiv: number;
  tiv_pct: number;
  amount_subject: number;
  amount_subject_pct: number;
  label?: string;
};
